import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { PriceLevel } from "../map/levels";
import { newId } from "../lib/id";

/**
 * An area a scenario is priced against.
 *
 * Only the identity is stored, never the prices: those are fetched fresh from
 * the API so a scenario saved months ago reflects the latest DVF release
 * rather than a snapshot frozen into localStorage.
 */
export interface ScenarioArea {
  scope: PriceLevel;
  code: string;
  name: string;
  /** Where on the map it was picked, so a comparison row can fly back to it. */
  lon: number;
  lat: number;
}

export type GrowthMode = "historic" | "custom";

/** One saved set of assumptions, and the areas it is being applied to. */
export interface Scenario {
  id: string;
  name: string;
  /** Flat size the scenario is priced at, in m². */
  surface: number;
  /** How many years forward to project. */
  horizon: number;
  /**
   * "historic" gives each area its own fitted trend, which is what makes a
   * comparison honest — two neighbourhoods rarely moved the same way.
   * "custom" applies one rate everywhere, for asking "what if the region
   * does X?".
   */
  growthMode: GrowthMode;
  /** Annual rate used in custom mode, as a fraction. */
  customRate: number;
  areas: ScenarioArea[];
  createdAt: number;
}

/** The inputs a projection needs — the part of a scenario the maths reads. */
export type ScenarioParams = Pick<
  Scenario,
  "surface" | "horizon" | "growthMode" | "customRate"
>;

export const SURFACE_MIN = 9;
export const SURFACE_MAX = 400;
export const HORIZON_MAX = 30;

const DEFAULTS: ScenarioParams = {
  surface: 60,
  horizon: 10,
  growthMode: "historic",
  customRate: 0.02,
};

const sameArea = (a: ScenarioArea, scope: string, code: string) =>
  a.scope === scope && a.code === code;

/** "Scenario 3" — the lowest number not already taken. */
function nextName(existing: Scenario[]): string {
  const taken = new Set(existing.map((s) => s.name));
  for (let i = 1; ; i++) {
    const name = `Scenario ${i}`;
    if (!taken.has(name)) return name;
  }
}

/**
 * Build a scenario from a partial.
 *
 * `id` and `createdAt` are applied last and are deliberately not overridable:
 * duplicating passes the whole source through here, and letting its id survive
 * would put two entries in the list under one identity — renaming or deleting
 * either would then hit both.
 */
function make(partial: Partial<Scenario>, existing: Scenario[]): Scenario {
  return {
    ...DEFAULTS,
    areas: [],
    ...partial,
    name: partial.name ?? nextName(existing),
    id: newId(),
    createdAt: Date.now(),
  };
}

interface ScenarioState {
  scenarios: Scenario[];
  activeId: string;

  /** The scenario every input and every panel currently edits. */
  active: () => Scenario;

  /** Start a fresh scenario on the defaults, and switch to it. */
  create: () => void;
  /** Copy one, areas and all, and switch to the copy. */
  duplicate: (id: string) => void;
  rename: (id: string, name: string) => void;
  /** Remove one. The list is never left empty — a fresh default takes over. */
  remove: (id: string) => void;
  activate: (id: string) => void;

  setSurface: (m2: number) => void;
  setHorizon: (years: number) => void;
  setGrowthMode: (mode: GrowthMode) => void;
  setCustomRate: (rate: number) => void;

  addArea: (area: ScenarioArea) => void;
  removeArea: (scope: PriceLevel, code: string) => void;
}

const INITIAL = make({ name: "Scenario 1" }, []);

export const useScenarioStore = create<ScenarioState>()(
  persist(
    (set, get) => {
      /** Apply a change to the active scenario, leaving the others alone. */
      const patch = (fn: (s: Scenario) => Partial<Scenario>) =>
        set((state) => ({
          scenarios: state.scenarios.map((s) =>
            s.id === state.activeId ? { ...s, ...fn(s) } : s,
          ),
        }));

      return {
        scenarios: [INITIAL],
        activeId: INITIAL.id,

        // Falls back to the first entry rather than throwing: a persisted
        // activeId can outlive the scenario it pointed at.
        active: () =>
          get().scenarios.find((s) => s.id === get().activeId) ??
          get().scenarios[0],

        create: () =>
          set((state) => {
            const next = make({}, state.scenarios);
            return { scenarios: [...state.scenarios, next], activeId: next.id };
          }),

        duplicate: (id) =>
          set((state) => {
            const source = state.scenarios.find((s) => s.id === id);
            if (!source) return state;
            const next = make(
              { ...source, name: `${source.name} copy`, areas: [...source.areas] },
              state.scenarios,
            );
            return { scenarios: [...state.scenarios, next], activeId: next.id };
          }),

        rename: (id, name) =>
          set((state) => ({
            scenarios: state.scenarios.map((s) =>
              s.id === id ? { ...s, name } : s,
            ),
          })),

        remove: (id) =>
          set((state) => {
            const rest = state.scenarios.filter((s) => s.id !== id);
            if (rest.length === 0) {
              const fresh = make({ name: "Scenario 1" }, []);
              return { scenarios: [fresh], activeId: fresh.id };
            }
            return {
              scenarios: rest,
              activeId: state.activeId === id ? rest[0].id : state.activeId,
            };
          }),

        activate: (activeId) => set({ activeId }),

        setSurface: (m2) =>
          patch(() => ({
            surface: Math.min(SURFACE_MAX, Math.max(SURFACE_MIN, Math.round(m2))),
          })),
        setHorizon: (years) =>
          patch(() => ({
            horizon: Math.min(HORIZON_MAX, Math.max(1, Math.round(years))),
          })),
        setGrowthMode: (growthMode) => patch(() => ({ growthMode })),
        setCustomRate: (customRate) => patch(() => ({ customRate })),

        addArea: (area) =>
          patch((s) =>
            s.areas.some((a) => sameArea(a, area.scope, area.code))
              ? {}
              : { areas: [...s.areas, area] },
          ),
        removeArea: (scope, code) =>
          patch((s) => ({ areas: s.areas.filter((a) => !sameArea(a, scope, code)) })),
      };
    },
    {
      name: "mete-scenario",
      version: 1,
      // v0 held a single implicit scenario as loose fields. Wrap it rather
      // than dropping it — those inputs are the user's, not derived state.
      migrate: (persisted, version) => {
        if (version >= 1) return persisted as never;
        const old = (persisted ?? {}) as Partial<ScenarioParams> & {
          pinned?: ScenarioArea[];
        };
        const only = make(
          {
            name: "Scenario 1",
            surface: old.surface ?? DEFAULTS.surface,
            horizon: old.horizon ?? DEFAULTS.horizon,
            growthMode: old.growthMode ?? DEFAULTS.growthMode,
            customRate: old.customRate ?? DEFAULTS.customRate,
            areas: old.pinned ?? [],
          },
          [],
        );
        return { scenarios: [only], activeId: only.id } as never;
      },
    },
  ),
);

/** The active scenario, re-rendering the caller whenever it changes. */
export function useActiveScenario(): Scenario {
  return useScenarioStore(
    (s) => s.scenarios.find((x) => x.id === s.activeId) ?? s.scenarios[0],
  );
}
